/**
 * @file App.tsx
 * @description Root component. Tries a silent refresh on first mount so
 *   reloads with a valid HttpOnly cookie don't flash through /login,
 *   then hands off to the router.
 * @module App
 * @security The bootstrap refresh fails silently and never reveals whether
 *   any session existed — same behavior the API exposes.
 */

import type { JSX } from 'react';
import { useEffect } from 'react';
import { AppRouter } from './router';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { Toaster } from './components/common/Toaster';
import { useAuthStore } from './stores/auth.store';
import { setAccessToken } from './lib/api';

export default function App(): JSX.Element {
  const setBootstrapped = useAuthStore((s) => s.setBootstrapped);

  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      try {
        const res = await fetch('/api/v1/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
          signal: ac.signal,
        });
        if (res.ok) {
          const data = (await res.json()) as { access_token: string };
          setAccessToken(data.access_token);
        }
      } catch {
        // Network or aborted — leave logged out.
      } finally {
        setBootstrapped(true);
      }
    })();
    return () => ac.abort();
  }, [setBootstrapped]);

  // ErrorBoundary wraps the whole router so a crash in ANY route falls
  // back to the recovery card instead of a blank page. Toaster sits
  // OUTSIDE the boundary on purpose: if the boundary itself ends up
  // rendering errors, we still want the toast container alive so the
  // recovery card's reload behaviour can surface fresh toasts on retry.
  return (
    <>
      <ErrorBoundary>
        <AppRouter />
      </ErrorBoundary>
      <Toaster />
    </>
  );
}
